// 发帖相关的 Action 层，负责业务逻辑和异步操作
import { action, makeObservable, runInAction } from 'mobx';
import store from '@stores/store';
import SetPostStore, { initPostData, getConfiguredVotes } from './store';
import { createPost, updatePost, uploadAttachment, readPostDetail, readThreadDetail, fetchDraft } from '@server';
import { uploadOssFile } from '@server/oss-upload'; // 导入 OSS 直传上传工具
import { BASE_IMG_URL } from '@images/imageUrls';
import { MAX_THREAD_IMAGES, countNormalImagesFromHtml } from '@utils/setPostLimits';
import { extractMentionsFromHtml, getMentionLimit, isMentionAdmin } from '@utils/setPostMentions';
import {
    isSameEditorContent,
    preserveEditorLineBreaks,
    sanitizeEditorInlineStyles,
} from '@utils/tiptapEditorContent';

const normalizeBooleanFlag = (value) => {
    if (value === true || value === 'true' || value === 1 || value === '1') return true;
    if (value === false || value === 'false' || value === 0 || value === '0') return false;
    return !!value;
};

const getHasCopyrightFromDetail = (detail) => {
    const rawAllowDownload = detail?.allowDownload ?? detail?.allow_download;
    if (typeof rawAllowDownload !== 'undefined' && rawAllowDownload !== null) {
        // allowDownload=true 表示「允许他人下载」，对应 hasCopyright=false（无版权保护）
        return !normalizeBooleanFlag(rawAllowDownload);
    }

    return normalizeBooleanFlag(detail?.hasCopyright ?? detail?.has_copyright);
};

class SetPostAction extends SetPostStore {
    constructor(props) {
        super(props);
        makeObservable(this);
    }

    /**
     * 同步发帖数据到 store
     * @param {object} data 要更新的数据
     */
    @action.bound
    setPostData(data) {
        try {
            const keys = Object.keys(data || {});
            this.addLog('setPostData', { keys, dataSnapshot: keys.reduce((acc, k) => { acc[k] = data[k]; return acc; }, {}) });
        } catch (_) {}
        Object.entries(data || {}).forEach(([key, value]) => {
            if (key === 'content' && typeof value === 'string') {
                const prev = this.postData.content || '';
                if (isSameEditorContent(prev, value)) {
                    return;
                }
            }
            if (key === 'problemId' || key === 'productId') {
                this.postData[key] = value === undefined || value === null || value === '' ? '' : String(value);
                return;
            }
            this.postData[key] = value;
        });
    }

    /**
     * 解析关联帖子链接，提取ID和类型
     * @param {string} url 帖子链接
     * @returns {{id:number,type:number,url:string}|null}
     */
    @action.bound
    parseCollectionUrl(url) {
        this.addLog('parseCollectionUrl:start', { url });
        if (!url) return null;
        const cleanUrl = url.trim().replace(/[?&]utm_source=[^&]*/ig, '');
        // 仅支持 vivo 社区域名
        const hostOk = /(?:bbs\.vivo\.com|club\.vivo\.com\.cn)/.test(cleanUrl);
        // 兼容 thread/detail，threadDetail，activity/detail，activityDetali 等路径
        const idMatch = cleanUrl.match(/(?:thread|threadDetail|detail|feedback|activity|activityDetali)[-\/]?(\d+)/i) || cleanUrl.match(/\/([0-9]+)(?:\.html)?(?:[/?#]|$)/);
        if (!hostOk || !idMatch) {
            this.addLog('parseCollectionUrl:invalid', { cleanUrl, hostOk, idMatch: !!idMatch });
            return null;
        }
        const id = Number(idMatch[1]);
        let type = 1;
        const lower = cleanUrl.toLowerCase();
        if (lower.includes('/feedback')) type = 2;
        else if (lower.includes('/activitydetali') || lower.includes('/activity')) type = 3;
        else if (lower.includes('/threaddetail') || lower.includes('/thread/')) type = 1;
        const parsed = { id, type, url: cleanUrl };
        this.addLog('parseCollectionUrl:success', parsed);
        return parsed;
    }

    /**
     * 将关联帖子写入 collection（去重、限制 10 条）
     */
    @action.bound
    addCollectionItem(item) {
        this.addLog('addCollectionItem', { resource_id: item.resource_id, type: item.type, title: item.title });
        const list = this.postData.collection || [];
        const filtered = list.filter(v => v.resource_id !== item.resource_id);
        const next = [...filtered, item].slice(0, 10);
        this.postData.collection = next;
    }

    /**
     * 移除已关联帖子
     */
    @action.bound
    removeCollectionItem(resourceId) {
        this.addLog('removeCollectionItem', { resourceId });
        this.postData.collection = (this.postData.collection || []).filter(v => (v.resource_id ?? v.id) !== resourceId);
    }

    /**
     * 获取帖子详情（用于编辑时回显）
     */
    async fetchPostDetail(id) {
        this.addLog('fetchPostDetail:start', { id });
        const res = await readPostDetail(id);
        const success = res?.code === 0 || res?.Code === 0;
        if (success) {
            const raw = res?.data || res?.Data;
            // 兼容 /api/v5/threads/:id 返回的多种形态
            const entity = Array.isArray(raw?.data) ? raw.data[0] : raw?.data;
            let normalized = raw;
            if (entity?.attributes) {
                normalized = { ...entity.attributes, id: entity.id, relationships: entity.relationships, included: raw?.included };
                if (entity.fixedAt || entity.fixed_at) {
                    normalized.fixedAt = entity.fixedAt || entity.fixed_at;
                }
            } else if (entity?.title || entity?.content) {
                normalized = { ...entity };
            }
            if (!normalized.fixedAt && (raw?.fixedAt || raw?.fixed_at || entity?.fixedAt || entity?.fixed_at)) {
                normalized.fixedAt = raw?.fixedAt || raw?.fixed_at || entity?.fixedAt || entity?.fixed_at;
            }
            this.addLog('fetchPostDetail:success', { id, title: normalized?.title, hasContent: !!normalized?.content, fixedAt: normalized?.fixedAt });
            return normalized;
        }
        this.addLog('fetchPostDetail:failed', { id, res });
        return null;
    }

    /**
     * 将帖子详情加载到表单（编辑模式）
     */
    @action.bound
    loadPostDetail(detail) {
        if (!detail) return;
        
        // 使用 runInAction 确保在 action 中执行，直接修改属性
        runInAction(() => {
            // 基础字段回显（带兼容）
            this.postData.id = detail.id || detail.threadId || detail.thread_id || this.postData.id;
            // 回显类型：接口返回 type 直接映射到 postData.type
            // 接口：0=普通贴，1=微贴
            // postData：0=普通贴，1=微贴
            if (typeof detail.type !== 'undefined') {
                this.postData.type = Number(detail.type);
            }
            this.postData.title = detail.title || detail.threadTitle || detail.question || detail.thread_title || '';
            this.postData.content = detail.content
                || detail.contentText
                || detail.postContent
                || detail.contentHtml
                || detail.contentHTML
                || detail.html
                || detail.message
                || (detail.firstPost && (detail.firstPost.content || detail.firstPost.contentHtml || detail.firstPost.contentHTML))
                || '';
            this.postData.isAigc = detail.isAigc || detail.aiGenerated || false;
            this.postData.hasCopyright = getHasCopyrightFromDetail(detail);
            
            // 处理话题信息 - 支持多种数据格式
            let topicIds = [];
            let topicsData = [];
            
            // 获取话题数据 - 兼容多种格式
            if (detail.topics && Array.isArray(detail.topics)) {
                topicsData = detail.topics;
            } else if (detail.threadTopics && Array.isArray(detail.threadTopics)) {
                topicsData = detail.threadTopics;
            } else if (detail.relationships?.topics?.data) {
                topicsData = detail.relationships.topics.data;
            }
            
            // 处理话题ID和缓存
            if (topicsData.length > 0) {
                topicIds = topicsData.map(t => String(t.id || t.topicId || t.topic_id));
                
                // 更新话题缓存，用于正确显示话题名称
                topicsData.forEach(topic => {
                    const id = String(topic.id || topic.topicId || topic.topic_id);
                    if (id && topic.name) {
                        this.topicCache[id] = {
                            id: id,
                            name: topic.name,
                            cover: topic.avatar || topic.cover || `${BASE_IMG_URL}/common/official2.png`,
                            count: `${topic.threadCount || topic.thread_count || 0}帖子`,
                            category: topic.category?.name || topic.categoryName || null, // 允许为null，后续通过话题详情获取
                        };
                    }
                });
                
                // 异步获取话题详情以补充分类信息（编辑模式下重要）
                setTimeout(() => {
                    this.fetchTopicDetailsForCategories(topicIds);
                }, 100);
            }
            
            this.postData.topicIds = topicIds;
            this.postData.votes = getConfiguredVotes(detail.votes);
            this.postData.collection = (detail.collection && detail.collection.length > 0) ? detail.collection : [];
            this.postData.form = detail.form || null;  // ✅ 添加：表单数据回显
            this.postData.coverId = detail.cover?.id || detail.coverId || detail.cover_id || 0;
            this.postData.videoId = detail.video?.id || detail.videoId || detail.video_id || 0;
            // 只有 cover 对象代表用户设置的帖子封面；普通帖的 coverUrl 可能只是正文首图。
            this.postData.coverImage = detail.cover?.url || '';
            const hasVideo = !!(detail.hasVideo || detail.video || detail.videoId || detail.video_id);
            this.postData.videoCoverUrl = detail.videoCoverUrl
                || detail.video?.coverUrl
                || detail.video?.videoCoverUrl
                || detail.video?.cover_url
                || (hasVideo ? detail.coverUrl : '')
                || '';
            this.postData.video = detail.video?.url || detail.video_url || detail.videoUrl || (detail.firstPost && detail.firstPost.videoUrl) || '';
            this.postData.images = (detail.images && detail.images.length > 0)
                ? detail.images
                : ((detail.firstPost && detail.firstPost.images) ? detail.firstPost.images : []);

            // 🔧 修复：加载定时发布设置，兼容多种字段名格式
            const fixedAtValue = detail.fixedAt || detail.fixed_at || detail.scheduledAt || detail.scheduled_at || null;
            console.log('[SetPostAction] loadPostDetail - fixedAt check:', { 
                'detail.fixedAt': detail.fixedAt, 
                'detail.fixed_at': detail.fixed_at,
                'fixedAtValue': fixedAtValue 
            });
            if (fixedAtValue) {
                // 如果有 fixedAt，设置为定时发布模式
                this.postData.publishMode = 'scheduled';
                this.postData.fixedAt = fixedAtValue;
                console.log('[SetPostAction] loadPostDetail - Setting scheduled mode:', { publishMode: 'scheduled', fixedAt: fixedAtValue });
                this.addLog('loadPostDetail:fixedAt', { fixedAt: fixedAtValue });
            } else {
                // 否则为立即发布模式
                this.postData.publishMode = 'immediate';
                this.postData.fixedAt = null;
                console.log('[SetPostAction] loadPostDetail - Setting immediate mode');
            }

            // 记录已存在的附件，避免更新时丢失
            const existingUploads = [];
            // 反馈帖图片独立于正文，展示和提交都读取 uploadedAttachments。
            if (this.postData.type === 2) {
                const seen = new Set();
                this.postData.images.forEach((image) => {
                    if (!image?.id || seen.has(String(image.id))) return;
                    seen.add(String(image.id));
                    const url = image.webpUrl || image.url || '';
                    existingUploads.push({ type: 1, id: image.id, url, localUrl: url });
                });
            }
            if (this.postData.coverId && this.postData.coverImage && this.postData.type !== 2) {
                existingUploads.push({ type: 1, id: this.postData.coverId, url: this.postData.coverImage, localUrl: this.postData.coverImage });
            }
            if (this.postData.videoId && this.postData.video) {
                existingUploads.push({ type: 3, id: this.postData.videoId, url: this.postData.video, localUrl: this.postData.video });
            }
            this.postData.uploadedAttachments = existingUploads;

            // 问题反馈字段回显
            const feedback = detail.feedback || {};
            this.postData.problemId = feedback.problem?.id || feedback.problemId || '';
            this.postData.problemName = feedback.problem?.name || feedback.problemName || '';
            this.postData.problemSolvedCount = Number(
                feedback.problem?.solvedCount ?? feedback.problemSolvedCount ?? feedback.solvedCount ?? 0
            ) || 0;
            this.postData.productId = feedback.product?.id || feedback.productId || '';
            this.postData.productName = feedback.product?.name || feedback.productName || '';
            this.postData.systemVersion = feedback.systemVersion || '';
            this.postData.imei = feedback.imei || '';
            this.postData.phone = feedback.phone || '';
            this.postData.logCode = feedback.logCode || '';
            
        });
    }

    /**
     * 批量获取话题详情以补充分类信息（编辑模式下使用）
     * @param {string[]} topicIds 话题ID数组
     */
    @action.bound
    async fetchTopicDetailsForCategories(topicIds) {
        if (!topicIds || topicIds.length === 0) return;
        
        this.addLog('fetchTopicDetailsForCategories:start', { topicIds });
        
        try {
            // 尝试导入topic相关的API，如果不存在则跳过
            let readTopicsByIds;
            try {
                const serverAPI = await import('@server');
                readTopicsByIds = serverAPI.readTopicsByIds || serverAPI.getTopicsByIds || serverAPI.fetchTopicsByIds;
            } catch (importError) {
                this.addLog('fetchTopicDetailsForCategories:import_failed', { error: importError.message });
                return; // 静默退出，不影响主流程
            }
            
            if (!readTopicsByIds) {
                this.addLog('fetchTopicDetailsForCategories:no_api', { message: 'readTopicsByIds API not found' });
                return;
            }
            
            // 批量请求话题详情
            const topicIdStr = topicIds.join(',');
            const response = await readTopicsByIds({ filter: { id: topicIdStr } });
            
            if (response?.code === 0 || response?.Code === 0) {
                const topicsDetail = response?.data?.data || response?.Data?.data || [];
                
                if (topicsDetail.length > 0) {
                    // 更新话题缓存中的分类信息
                    let hasUpdates = false;
                    topicsDetail.forEach(topic => {
                        const id = String(topic.id);
                        const categoryName = topic.category?.name;
                        
                        if (categoryName && this.topicCache[id]) {
                            this.topicCache[id].category = categoryName;
                            hasUpdates = true;
                            this.addLog('fetchTopicDetailsForCategories:updated', { 
                                topicId: id, 
                                topicName: topic.name, 
                                category: categoryName 
                            });
                        }
                    });
                    
                    if (hasUpdates) {
                        this.addLog('fetchTopicDetailsForCategories:success', { 
                            updatedCount: topicsDetail.length 
                        });
                        
                        // 触发UI更新（通过触发一个微小的数据变更）
                        this.postData.topicIds = [...this.postData.topicIds];
                    }
                }
            } else {
                this.addLog('fetchTopicDetailsForCategories:api_failed', { response });
            }
        } catch (error) {
            this.addLog('fetchTopicDetailsForCategories:error', { 
                message: error?.message,
                topicIds 
            });
            // 静默失败，不影响主流程
        }
    }
    
    /**
     * 通过现有的topic store获取话题详情（备用方案）
     * @param {string[]} topicIds 话题ID数组
     * @param {any} topicStore 话题store实例
     */
    @action.bound
    async fetchTopicDetailsViaStore(topicIds, topicStore) {
        if (!topicIds || topicIds.length === 0 || !topicStore) return;
        
        this.addLog('fetchTopicDetailsViaStore:start', { topicIds });
        
        try {
            // 尝试使用topic store的readTopicsByIds方法
            if (topicStore.readTopicsByIds) {
                const topicIdStr = topicIds.join(',');
                const response = await topicStore.readTopicsByIds({ filter: { id: topicIdStr } });
                
                if (response?.code === 0 || response?.Code === 0) {
                    const topicsDetail = response?.data?.data || response?.Data?.data || [];
                    
                    if (topicsDetail.length > 0) {
                        // 更新话题缓存中的分类信息
                        let hasUpdates = false;
                        topicsDetail.forEach(topic => {
                            const id = String(topic.id);
                            const categoryName = topic.category?.name;
                            
                            if (categoryName && this.topicCache[id]) {
                                this.topicCache[id].category = categoryName;
                                hasUpdates = true;
                                this.addLog('fetchTopicDetailsViaStore:updated', { 
                                    topicId: id, 
                                    topicName: topic.name, 
                                    category: categoryName 
                                });
                            }
                        });
                        
                        if (hasUpdates) {
                            this.addLog('fetchTopicDetailsViaStore:success', { 
                                updatedCount: topicsDetail.length 
                            });
                            
                            // 触发UI更新
                            this.postData.topicIds = [...this.postData.topicIds];
                        }
                    }
                } else {
                    this.addLog('fetchTopicDetailsViaStore:api_failed', { response });
                }
            } else {
                this.addLog('fetchTopicDetailsViaStore:no_method', { message: 'readTopicsByIds method not found in topic store' });
            }
        } catch (error) {
            this.addLog('fetchTopicDetailsViaStore:error', { 
                message: error?.message,
                topicIds 
            });
            // 静默失败，不影响主流程
        }
    }

    /**
     * 通过链接获取帖子详情并写入 collection
     * @param {string} url 帖子链接
     * @returns {Promise<{code:number,message?:string,data?:object}>}
     */
    @action.bound
    async fetchCollectionThread(url) {
        this.addLog('fetchCollectionThread:start', { url });
        const parsed = this.parseCollectionUrl(url);
        if (!parsed) {
            this.addLog('fetchCollectionThread:invalid_url', { url });
            return { code: -1, message: '请输入正确的 vivo 社区帖子链接' };
        }

        let ret;
        // 根据URL类型判断调用哪个API
        if (parsed.type === 3) {
            // 活动贴：调用 readActivityDetail
            try {
                const { readActivityDetail } = await import('@server');
                if (readActivityDetail) {
                    ret = await readActivityDetail(parsed.id);
                } else {
                    this.addLog('fetchCollectionThread:no_activity_api', { url });
                    return { code: -1, message: '活动贴API不可用' };
                }
            } catch (importError) {
                this.addLog('fetchCollectionThread:activity_import_error', { error: importError.message });
                return { code: -1, message: '获取活动贴失败' };
            }
        } else {
            // 普通贴：调用 readThreadDetail
            ret = await readThreadDetail(parsed.id, {});
        }

        if (ret.code !== 0 && ret.Code !== 0) {
            this.addLog('fetchCollectionThread:api_failed', { parsed, ret });
            return ret;
        }

        const data = ret.data || ret.Data || {};
        const item = {
            url: parsed.url,
            resource_id: parsed.id,
            type: data.type ?? parsed.type ?? 1,
            title: data?.title|| '',
            summary: data.summary || data.summaryText || data.description || data.contentText || '',
            cover: data.cover || null,
            cover_url: data.cover?.url || data.cover_url || data.coverUrl || data.thumbUrl || data?.cover?.thumbUrl || '',
            images: Array.isArray(data.images) ? data.images : [],
            user: data.user || data.userInfo || null,
            avatar: data.user?.avatar || data.userInfo?.avatar || '',
            post_count: data.postCount || data.post_count || 0,
            like_count: data.likeCount || data.like_count || 0,
        };

        this.addLog('fetchCollectionThread:success', { parsed, itemTitle: item.title });
        this.addCollectionItem(item);
        return { code: 0, data: item };
    }

    /**
     * 设置加载状态
     * @param {string} type 加载类型
     * @param {boolean} status 加载状态
     */
    @action.bound
    setLoadingStatus(type, status) {
        this.addLog('setLoadingStatus', { type, status });
        this.loading[type] = status;
    }

    /**
     * 添加话题ID
     * @param {number} topicId 话题ID
     */
    @action.bound
    addTopicId(topicId) {
        this.addLog('addTopicId', { topicId });
        if (!this.postData.topicIds.includes(topicId)) {
            this.postData.topicIds = [...this.postData.topicIds, topicId];
        }
    }

    /**
     * 移除话题ID
     * @param {number} topicId 话题ID
     */
    @action.bound
    removeTopicId(topicId) {
        this.addLog('removeTopicId', { topicId });
        this.postData.topicIds = this.postData.topicIds.filter(id => id !== topicId);
    }

    /**
     * 批量设置投票数据（覆盖现有投票）
     * @param {array} votes 投票数组
     */
    @action.bound
    setVotes(votes) {
        this.addLog('setVotes', { count: Array.isArray(votes) ? votes.length : 0 });
        if (!Array.isArray(votes)) {
            this.postData.votes = [];
            return;
        }
        this.postData.votes = votes.map(vote => {
            // 如果vote有id（编辑模式），保留所有原始字段
            if (typeof vote.id === 'number') {
                return {
                    ...vote,
                    // 确保基本字段正确
                    type: vote.type || 0,
                    title: vote.title || '',
                    choiceType: vote.choiceType || (vote.voteType === 'single' ? 1 : 2),
                    expiredAt: vote.expiredAt || vote.endTime || '',
                    options: (vote.options || []).map(opt => {
                        // 如果option有id，保留所有原始字段
                        if (typeof opt.id === 'number') {
                            return {
                                ...opt,
                                content: opt.text || opt.content || '',
                            };
                        }
                        // 新增option只包含content
                        return {
                            content: opt.text || opt.content || '',
                        };
                    }),
                };
            }
            // 新建vote，只包含基本字段
            return {
                type: vote.type || 0,
                title: vote.title || '',
                choiceType: vote.choiceType || (vote.voteType === 'single' ? 1 : 2),
                expiredAt: vote.expiredAt || vote.endTime || '',
                options: (vote.options || []).map(opt => ({
                    content: opt.text || opt.content || '',
                })),
                ...(vote.type === 1 ? {
                    officialScore: vote.officialScore || 100,
                    perScore: vote.perScore || 10,
                } : {}),
            };
        });
    }

    /**
     * 添加投票
     * @param {object} vote 投票对象
     * @param {number} vote.type 投票类型 0-普通投票 1-积分投票
     * @param {string} vote.title 投票标题
     * @param {number} vote.choiceType 选择类型 1-单选 2-多选
     * @param {string} vote.expiredAt 过期时间
     * @param {array} vote.options 选项数组
     * @param {number} [vote.officialScore] 官方积分（仅积分投票）
     * @param {number} [vote.perScore] 每次投票积分（仅积分投票）
     */
    @action.bound
    addVote(vote) {
        this.addLog('addVote', { type: vote.type, title: vote.title });
        const newVote = {
            type: vote.type || 0,
            title: vote.title || '',
            choiceType: vote.choiceType || 1,
            expiredAt: vote.expiredAt || '',
            options: vote.options || [],
        };

        // 如果是积分投票，添加积分相关字段
        if (vote.type === 1) {
            newVote.officialScore = vote.officialScore || 100;
            newVote.perScore = vote.perScore || 10;
        }

        this.postData.votes = [...this.postData.votes, newVote];
    }

    /**
     * 更新投票
     * @param {number} index 投票索引
     * @param {object} voteData 投票数据
     */
    @action.bound
    updateVote(index, voteData) {
        this.addLog('updateVote', { index, voteData });
        if (this.postData.votes[index]) {
            this.postData.votes[index] = {
                ...this.postData.votes[index],
                ...voteData,
            };
            // 触发响应式更新
            this.postData.votes = [...this.postData.votes];
        }
    }

    /**
     * 删除投票
     * @param {number} index 投票索引
     */
    @action.bound
    removeVote(index) {
        this.addLog('removeVote', { index });
        this.postData.votes = this.postData.votes.filter((_, i) => i !== index);
    }

    /**
     * 添加投票选项
     * @param {number} voteIndex 投票索引
     * @param {string} content 选项内容
     */
    @action.bound
    addVoteOption(voteIndex, content) {
        this.addLog('addVoteOption', { voteIndex, content });
        if (this.postData.votes[voteIndex]) {
            this.postData.votes[voteIndex].options = [
                ...this.postData.votes[voteIndex].options,
                { content },
            ];
            // 触发响应式更新
            this.postData.votes = [...this.postData.votes];
        }
    }

    /**
     * 更新投票选项
     * @param {number} voteIndex 投票索引
     * @param {number} optionIndex 选项索引
     * @param {string} content 选项内容
     */
    @action.bound
    updateVoteOption(voteIndex, optionIndex, content) {
        this.addLog('updateVoteOption', { voteIndex, optionIndex, content });
        if (this.postData.votes[voteIndex]?.options[optionIndex]) {
            this.postData.votes[voteIndex].options[optionIndex].content = content;
            // 触发响应式更新
            this.postData.votes = [...this.postData.votes];
        }
    }

    /**
     * 删除投票选项
     * @param {number} voteIndex 投票索引
     * @param {number} optionIndex 选项索引
     */
    @action.bound
    removeVoteOption(voteIndex, optionIndex) {
        this.addLog('removeVoteOption', { voteIndex, optionIndex });
        if (this.postData.votes[voteIndex]) {
            this.postData.votes[voteIndex].options = this.postData.votes[voteIndex].options.filter(
                (_, i) => i !== optionIndex
            );
            // 触发响应式更新
            this.postData.votes = [...this.postData.votes];
        }
    }

    /**
     * 重置发帖数据
     */
    @action.bound
    resetPostData() {
        this.addLog('resetPostData');
        this.postData = { ...initPostData };
    }

    /**
     * 上传附件（支持去重）
     * @param {File} file 文件对象
     * @param {number} type 附件类型 1-图片 2-视频 3-视频（使用 OSS 直传）
     * @param {string} localUrl 本地路径（用于去重判断）
     * @returns {Promise<object>} 返回结果 { code, data: { id, url } }
     */
    @action.bound
    async uploadAttachment(file, type, localUrl) {
        this.addLog('uploadAttachment:start', { type, name: file?.name, size: file?.size, localUrl });
        try {
            // 检查是否已经上传过该文件
            const existingAttachment = this.postData.uploadedAttachments.find(
                att => att.localUrl === localUrl && att.type === type
            );
            if (existingAttachment) {
                this.addLog('uploadAttachment:dedup_hit', { existingAttachment });
                return { code: 0, data: existingAttachment };
            }

            // 处理视频上传：使用 OSS 直传（type === 3）
            if (type === 3) {
                this.addLog('uploadAttachment:using_oss_upload', { fileName: file?.name });
                
                // 1. 直传到 OSS
                const ossResult = await uploadOssFile(
                    file,
                    'public/videos/' + new Date().toISOString().split('T')[0].replace(/-/g, '/'),
                    (percent) => {
                        this.addLog('uploadAttachment:oss_progress', { percent });
                    }
                );

                if (ossResult.code !== 0) {
                    this.addLog('uploadAttachment:oss_failed', { ossResult });
                    return ossResult;
                }

                // OSS 回调已注册附件，发布时使用返回的真实附件 ID。
                const attachmentData = {
                    type: 3,
                    id: ossResult.data.id,
                    url: ossResult.data?.url || ossResult.data?.name || '',
                    localUrl,
                    ossUrl: ossResult.data?.url, // 保存 OSS 原始 URL
                };

                this.postData.uploadedAttachments = [
                    ...this.postData.uploadedAttachments,
                    attachmentData,
                ];

                this.addLog('uploadAttachment:oss_success', { attachment: attachmentData });
                return { code: 0, data: attachmentData };
            }

            // 处理图片或其他文件：使用传统 API 上传
            const formData = new FormData();
            formData.append('type', String(type)); // 1-图片 2-视频
            formData.append('file', file);
            const ret = await uploadAttachment(formData);
            // 兼容不同响应格式：{ code, data } 或 { Code, Data }
            const code = (typeof ret?.code !== 'undefined') ? ret.code : ret?.Code;
            const data = ret?.data ?? ret?.Data;
            
            if (code === 0 && data?.id) {
                // 记录已上传的附件
                const attachmentData = {
                    type,
                    id: data.id,
                    // 图片优先使用 webpUrl，其他类型回退到原始 URL
                    url: data.webpUrl || data.url || data.ossVideoUrl || localUrl,
                    localUrl,
                };
                this.postData.uploadedAttachments = [
                    ...this.postData.uploadedAttachments,
                    attachmentData,
                ];
                this.addLog('uploadAttachment:success', { type, attachment: attachmentData });
                return { code: 0, data: attachmentData };
            }
            this.addLog('uploadAttachment:api_failed', { type, ret });
            return ret;
        } catch (error) {
            this.addLog('uploadAttachment:error', { type, message: error?.message });
            return { code: -1, message: error.message || '上传失败' };
        }
    }

    /**
     * 设置封面图（本地路径）
     * @param {string} url 封面图URL
     */
    @action.bound
    setCoverImage(url) {
        this.addLog('setCoverImage', { url });
        this.postData.coverImage = url;
        this.postData.coverImageUploaded = false; // 标记为未上传
    }

    /**
     * 设置视频（本地路径）
     * @param {string} url 视频URL
     */
    @action.bound
    setVideo(url) {
        this.addLog('setVideo', { url });
        this.postData.video = url;
        this.postData.videoUploaded = false; // 标记为未上传
    }

    /**
     * 上传所有待上传的附件（封面图、视频等）
     * @param {File} coverFile 封面图文件对象（可选）
     * @param {File} videoFile 视频文件对象（可选）
     * @returns {Promise<object>} { success: boolean, message: string }
     */
    @action.bound
    async uploadAllAttachments(coverFile, videoFile) {
        this.addLog('uploadAllAttachments:start', { hasCoverFile: !!coverFile, hasVideoFile: !!videoFile });
        const results = { success: true, message: '', attachments: {} };

        try {
            // 上传封面图（仅上传手动选择的封面，不上传视频自动封面）
            if (coverFile && this.postData.coverImage && !this.postData.coverImageUploaded) {
                const uploadRes = await this.uploadAttachment(coverFile, 1, this.postData.coverImage);
                if (uploadRes.code === 0) {
                    this.postData.coverId = uploadRes.data.id;
                    this.postData.coverImageUploaded = true;
                    results.attachments.cover = uploadRes.data;
                    this.addLog('uploadAllAttachments:cover_success', { attachment: uploadRes.data });
                } else {
                    results.success = false;
                    results.message = uploadRes.message || '封面图上传失败';
                    this.addLog('uploadAllAttachments:cover_failed', { message: results.message });
                    return results;
                }
            }

            // 视频上传已在选择时完成（TiptapEditor handleVideoChange 中）
            // 此处不再重复上传视频，直接从已上传附件中获取 videoId
            // videoId 已经在 uploadAttachment 中处理过，无需在此重复处理

            this.addLog('uploadAllAttachments:done', results);
            return results;
        } catch (error) {
            this.addLog('uploadAllAttachments:error', { message: error?.message });
            return { success: false, message: error.message || '上传失败' };
        }
    }

    /**
     * 检查内容是否有效（包含文本或媒体）
     * 需要同时检查：文本内容、图片、视频等
     */
    hasValidContent(content) {
        if (!content) return false;
        
        // 检查是否有文本内容（移除 HTML 标签）
        const cleanContent = content.replace(/<[^>]*>/g, '').trim();
        if (cleanContent) return true;
        
        // 检查是否有图片（包括表情图片）
        if (/<img[^>]*>/i.test(content)) return true;
        
        // 检查是否有视频
        if (/<video[^>]*>/i.test(content)) return true;
        
        // 检查是否有音频
        if (/<audio[^>]*>/i.test(content)) return true;
        
        return false;
    }

    /**
     * 验证发帖数据
     * @returns {object} { valid: boolean, message: string }
     */
    validatePostData() {
        const { type, title, content, votes, topicIds, problemId, productId, systemVersion } = this.postData;

        if (type === 2) {
            if (!problemId) {
                const ret = { valid: false, message: '请选择问题类型' };
                this.addLog('validatePostData:invalid', ret);
                return ret;
            }
            if (!productId) {
                const ret = { valid: false, message: '请选择手机型号' };
                this.addLog('validatePostData:invalid', ret);
                return ret;
            }
            if (!systemVersion || !String(systemVersion).trim()) {
                const ret = { valid: false, message: '请输入软件版本号' };
                this.addLog('validatePostData:invalid', ret);
                return ret;
            }
        } else if (!Array.isArray(topicIds) || topicIds.length === 0) {
            const ret = { valid: false, message: '请选择话题' };
            this.addLog('validatePostData:invalid', ret);
            return ret;
        }

        // 标题仅在 type === 0 时校验
        if (type === 0) {
            if (!title || !title.trim()) {
                const ret = { valid: false, message: '请输入标题' };
                this.addLog('validatePostData:invalid', ret);
                return ret;
            }
            if (title.length > 30) {
                const ret = { valid: false, message: '标题最多30字' };
                this.addLog('validatePostData:invalid', ret);
                return ret;
            }
        }

        // 检查内容是否有效（包含文本或媒体）
        if (!this.hasValidContent(content)) {
            const ret = { valid: false, message: '请输入内容' };
            this.addLog('validatePostData:invalid', ret);
            return ret;
        }

        const mentionCount = extractMentionsFromHtml(content).length || (Array.isArray(this.postData?.mentions) ? this.postData.mentions.length : 0);
        // 发布校验不要依赖 this.props（setPost store 通常没有注入 user）
        const rootStore = store.getStore?.();
        const userInfo = rootStore?.user?.userInfo || this.props?.user?.userInfo;
        const isAdmin = isMentionAdmin(userInfo);
        const mentionLimit = getMentionLimit(userInfo);
        if (Number.isFinite(mentionLimit) && mentionCount > mentionLimit) {
            const ret = { valid: false, message: `为维护友好的交流氛围，单条内容最多支持@${mentionLimit}位好友` };
            this.addLog('validatePostData:invalid', { ...ret, mentionCount, isAdmin, hasUserInfo: !!userInfo });
            return ret;
        }

        const normalImageCount = countNormalImagesFromHtml(content);
        if (normalImageCount > MAX_THREAD_IMAGES) {
            const ret = { valid: false, message: `图文内容最多上传 ${MAX_THREAD_IMAGES} 张图片` };
            this.addLog('validatePostData:invalid', { ...ret, normalImageCount });
            return ret;
        }

        // 验证投票数据
        const configuredVotes = getConfiguredVotes(votes);
        if (configuredVotes.length > 0) {
            for (let i = 0; i < configuredVotes.length; i++) {
                const vote = configuredVotes[i];
                
                if (!vote.title || !vote.title.trim()) {
                    return { valid: false, message: `第${i + 1}个投票缺少标题` };
                }

                if (!vote.options || vote.options.length < 2) {
                    return { valid: false, message: `第${i + 1}个投票至少需要2个选项` };
                }

                for (let j = 0; j < vote.options.length; j++) {
                    const optionContent = (vote.options[j].content || vote.options[j].text || '').trim();
                    if (!optionContent) {
                        const ret = { valid: false, message: `第${i + 1}个投票的第${j + 1}个选项不能为空` };
                        this.addLog('validatePostData:invalid', ret);
                        return ret;
                    }
                }

                if (vote.type === 1) {
                    if (!vote.officialScore || vote.officialScore <= 0) {
                        const ret = { valid: false, message: `第${i + 1}个积分投票缺少官方积分` };
                        this.addLog('validatePostData:invalid', ret);
                        return ret;
                    }
                    if (!vote.perScore || vote.perScore <= 0) {
                        const ret = { valid: false, message: `第${i + 1}个积分投票缺少每次投票积分` };
                        this.addLog('validatePostData:invalid', ret);
                        return ret;
                    }
                }
            }
        }
        const ok = { valid: true, message: '' };
        this.addLog('validatePostData:ok');
        return ok;
    }

    /**
     * 解析反馈帖视频 ID（视频走 videoId 字段，不进 attachments）
     */
    resolveFeedbackVideoId() {
        const { videoId, video, uploadedAttachments = [] } = this.postData;
        const numericVideoId = Number(videoId);
        if (!Number.isNaN(numericVideoId) && numericVideoId > 0) {
            return numericVideoId;
        }
        if (video) {
            const videoAttachment = uploadedAttachments.find((att) => att.type === 3);
            const attachmentVideoId = Number(videoAttachment?.id);
            if (!Number.isNaN(attachmentVideoId) && attachmentVideoId > 0) {
                return attachmentVideoId;
            }
        }
        return 0;
    }

    /**
     * 组装反馈帖 attachments 参数（仅图片）：[{ id: "31189" }, { id: "31190" }]
     */
    buildFeedbackAttachments() {
        const { uploadedAttachments = [] } = this.postData;
        const ids = [];
        const seen = new Set();

        uploadedAttachments
            .filter((att) => att?.type === 1)
            .forEach((att) => {
                if (att?.id === undefined || att?.id === null || att?.id === '') return;
                const key = String(att.id);
                if (!seen.has(key)) {
                    seen.add(key);
                    ids.push(key);
                }
            });

        return ids.map((id) => ({ id }));
    }

    /**
     * 组装反馈帖发布参数（/api/v3/thread.create，type=2）
     */
    buildFeedbackCreateParams(cleanedContent) {
        const { source, coverId, problemId, productId, systemVersion, imei, phone, logCode } = this.postData;
        const resolvedVideoId = this.resolveFeedbackVideoId();

        const params = {
            type: 2,
            content: cleanedContent.trim(),
            source,
            coverId: coverId || 0,
            attachments: this.buildFeedbackAttachments(),
            feedback: {
                problemId: Number(problemId),
                productId: Number(productId),
                imei: (imei || '').trim(),
                logCode: (logCode || '').trim(),
                phone: (phone || '').trim(),
                systemVersion: (systemVersion || '').trim(),
            },
        };

        if (resolvedVideoId > 0) {
            params.videoId = resolvedVideoId;
        }

        return params;
    }

    /**
     * 获取格式化后的发帖参数（需要先上传附件）
     * @returns {object} 发帖参数
     */
    @action
    getCreatePostParams() {
        const { type, title, content, source, coverId, videoId, topicIds, votes, form, video, uploadedAttachments, collection = [], isAigc = false, hasCopyright = false, publishMode = 'immediate', fixedAt = null } = this.postData;

        // 保留编辑器文字颜色，清理其他行内样式，并将纯空段落输出为独立 br
        let cleanedContent = preserveEditorLineBreaks(
            sanitizeEditorInlineStyles(content || '')
        );

        // 问题反馈帖：走独立参数结构
        if (type === 2) {
            const params = this.buildFeedbackCreateParams(cleanedContent);
            try {
                this.addLog('getCreatePostParams:feedback', {
                    contentLength: params.content.length,
                    attachmentCount: params.attachments.length,
                    problemId: params.feedback.problemId,
                    productId: params.feedback.productId,
                });
            } catch (_) {}
            return params;
        }

        const normalizedVotes = getConfiguredVotes(votes);

        const params = {
            type: type,
            title: title.trim(),
            content: cleanedContent.trim(),
            source,
            topicIds: [...topicIds],
            votes: normalizedVotes.map(vote => {
                // 如果是已有vote（有id），保留所有原始字段
                if (typeof vote.id === 'number') {
                    return {
                        ...vote,
                        title: (vote.title || '').trim(),
                        options: vote.options.map(opt => {
                            const optionContent = (opt.content || opt.text || '').trim();
                            // 如果是已有option（有id），保留所有原始字段
                            if (typeof opt.id === 'number') {
                                return {
                                    ...opt,
                                    content: optionContent,
                                };
                            }
                            // 新增option只包含content
                            return {
                                content: optionContent,
                            };
                        }),
                    };
                }
                // 新建vote，只包含基本字段
                const voteData = {
                    type: vote.type,
                    title: (vote.title || '').trim(),
                    choiceType: vote.choiceType,
                    expiredAt: vote.expiredAt,
                    options: vote.options.map(opt => ({
                        content: (opt.content || opt.text || '').trim(),
                    })),
                };

                if (vote.type === 1) {
                    voteData.officialScore = vote.officialScore;
                    voteData.perScore = vote.perScore;
                }

                return voteData;
            }),
        };

        // 帖子封面独立提交，不受视频影响
        // 当 coverId 为 0 时也要传递，表示删除封面
        if (coverId || coverId === 0) {
            params.coverId = coverId;
        }

        // 关联帖子
        if (collection && collection.length) {
            params.collection = collection.map(item => ({
                resource_id: item.resource_id || item.id,
                type: item.type || 1,
                url: item.url,
            }));
        }

        // AI内容声明
        params.isAigc = !!isAigc;

        // 版权保护（不允许他人下载图片）
        params.hasCopyright = !!hasCopyright;
        // hasCopyright=true 表示「版权保护开启」，对应 allowDownload=false（不允许下载）
        params.allowDownload = !hasCopyright;

        // 定时发布字段
        if (publishMode === 'scheduled' && fixedAt) {
            // 如果是 dayjs 对象，转换为 ISO 字符串
            if (fixedAt.format) {
                // dayjs 对象
                params.fixedAt = fixedAt.format('YYYY-MM-DD HH:mm:ss');
            } else if (typeof fixedAt === 'string') {
                // 字符串格式，直接传递
                params.fixedAt = fixedAt;
            } else if (fixedAt instanceof Date) {
                // Date 对象
                params.fixedAt = fixedAt.toISOString().slice(0, 19).replace('T', ' ');
            }
        }

        // 表单数据：检查是否有表单字段数据
        if (form && typeof form === 'object' && Array.isArray(form.fields) && form.fields.length > 0) {
            // 新发布表单时总是发送；只有当表单有用户提交过时才不发送字段
            const userCount = form.userCount || 0;
            if (userCount <= 0) {
                params.form = form;
                console.log('[SetPostAction] Form sent:', { fieldsCount: form.fields.length, userCount });
            } else {
                console.log('[SetPostAction] Form has submissions (userCount > 0), skipping form fields in update');
                this.addLog('getCreatePostParams:form_not_sent', { userCount });
            }
        }

        // 处理视频ID
        if (video) {
            // 如果有视频，从已上传附件或已存在记录中获取视频ID
            const videoAttachment = uploadedAttachments.find(att => att.type === 3 && att.localUrl === video);
            const finalVideoId = videoId || videoAttachment?.id;
            if (typeof finalVideoId === 'number' && finalVideoId > 0) {
                params.videoId = finalVideoId;
            }
        } else if (videoId === 0) {
            // 如果视频被删除，videoId 为 0，需要传递给后端表示删除视频
            params.videoId = 0;
        }

        try {
            this.addLog('getCreatePostParams', {
                type,
                titleLength: (title || '').length,
                contentLength: (content || '').length,
                topicCount: topicIds?.length || 0,
                votesCount: votes?.length || 0,
                hasForm: !!form,
                hasVideo: !!video,
                isAigc: !!isAigc,
                coverId: coverId ?? null,
                publishMode,
                hasFixedAt: !!fixedAt,
            });
        } catch (_) {}
        
        return params;
    }

    /**
     * 获取编辑帖子参数
     * @returns {object} 帖子更新参数
     */
    @action
    getUpdatePostParams() {
        const params = this.getCreatePostParams();
        const id = this.postData.id || this.postData.threadId;
        if (id) {
            const parsedId = Number(id);
            params.id = Number.isNaN(parsedId) ? id : parsedId;
        }
        // 普通帖编辑模式传递 videoId；反馈帖视频也走 videoId 字段
        if (this.postData.type === 2) {
            params.videoId = this.resolveFeedbackVideoId();
        } else {
            const currentVideoId = this.postData.videoId;
            params.videoId = typeof currentVideoId === 'number' ? currentVideoId : 0;
        }
        return params;
    }

    /**
     * 创建帖子（先上传附件再发布）
     * @param {File} coverFile 封面图文件对象（可选）
     * @param {File} videoFile 视频文件对象（可选）
     * @returns {Promise<object>} 返回结果
     */
    @action.bound
    async createPost(coverFile = null, videoFile = null) {
        this.addLog('createPost:start', { hasCoverFile: !!coverFile, hasVideoFile: !!videoFile });
        // 验证数据
        const validation = this.validatePostData();
        if (!validation.valid) {
            this.addLog('createPost:validation_failed', validation);
            return { code: -1, message: validation.message };
        }

        this.setLoadingStatus('submit', true);

        try {
            // 1. 先上传所有附件
            const uploadResult = await this.uploadAllAttachments(coverFile, videoFile);
            if (!uploadResult.success) {
                this.addLog('createPost:attachments_failed', uploadResult);
                return { code: -1, message: uploadResult.message };
            }

            // 2. 组合发帖参数
            const params = this.getCreatePostParams();
            this.addLog('createPost:request', { params });
            
            // 3. 调用发帖接口
            const ret = await createPost(params);
            if (ret.code === 0) {
                this.addLog('createPost:success', { ret });
                this.resetPostData();
            }
            else {
                this.addLog('createPost:api_failed', { ret });
            }
            return ret;
        } catch (error) {
            this.addLog('createPost:error', { message: error?.message });
            return { code: -1, message: error.message || '发布失败' };
        } finally {
            this.setLoadingStatus('submit', false);
            this.addLog('createPost:done');
        }
    }

    /**
     * 编辑帖子（先上传附件再更新）
     */
    @action.bound
    async updatePost(coverFile = null, videoFile = null) {
        this.addLog('updatePost:start', { hasCoverFile: !!coverFile, hasVideoFile: !!videoFile, id: this.postData?.id });
        const validation = this.validatePostData();
        if (!validation.valid) {
            this.addLog('updatePost:validation_failed', validation);
            return { code: -1, message: validation.message };
        }

        if (!this.postData?.id) {
            const ret = { code: -1, message: '缺少帖子ID，无法编辑' };
            this.addLog('updatePost:missing_id', ret);
            return ret;
        }

        this.setLoadingStatus('submit', true);

        try {
            // 1. 上传附件（如有变更）
            const uploadResult = await this.uploadAllAttachments(coverFile, videoFile);
            if (!uploadResult.success) {
                this.addLog('updatePost:attachments_failed', uploadResult);
                return { code: -1, message: uploadResult.message };
            }

            // 2. 组装参数
            const params = this.getUpdatePostParams();
            this.addLog('updatePost:request', { params });

            // 3. 调用更新接口
            const ret = await updatePost(params);
            if (ret.code === 0) {
                this.addLog('updatePost:success', { ret });
            } else {
                this.addLog('updatePost:api_failed', { ret });
            }
            return ret;
        } catch (error) {
            this.addLog('updatePost:error', { message: error?.message });
            return { code: -1, message: error.message || '编辑失败' };
        } finally {
            this.setLoadingStatus('submit', false);
            this.addLog('updatePost:done');
        }
    }

    /**
     * 上传封面
     * @param {File} file 文件对象
     * @returns {Promise<object>} 返回结果
     */
    @action.bound
    async uploadCover(file) {
        this.addLog('uploadCover:start', { name: file?.name, size: file?.size });
        this.setLoadingStatus('uploadCover', true);
        try {
            const formData = new FormData();
            formData.append('type', '1'); // 1-帖子图片
            formData.append('file', file);
            const ret = await uploadAttachment(formData);
            if (ret.code === 0 && ret.data?.id) {
                this.setPostData({ coverId: ret.data.id });
                this.addLog('uploadCover:success', { id: ret.data.id });
            }
            else {
                this.addLog('uploadCover:api_failed', { ret });
            }
            return ret;
        } catch (error) {
            this.addLog('uploadCover:error', { message: error?.message });
            return { code: -1, message: error.message || '上传失败' };
        } finally {
            this.setLoadingStatus('uploadCover', false);
            this.addLog('uploadCover:done');
        }
    }

    /**
     * 查询是否有草稿
     * @param {number} type 帖子类型，反馈帖传 2，普通贴传 0，默认 0
     * @returns {Promise<object>} 返回结果
     */
    @action.bound
    async checkDraft(type = 0) {
        this.addLog('checkDraft:start', { type });
        try {
            const draftType = type === 2 ? 2 : 0;
            const ret = await fetchDraft({ type: draftType, clear: 0 });
            if (ret.code === 0 || ret.Code === 0) {
                const data = ret.Data || ret.data;
                const hasDraft = data.hasDraft || false;
                const threadId = data.threadId || 0;
                runInAction(() => {
                    this.draftData.hasDraft = hasDraft;
                    this.draftData.draftThreadId = hasDraft && threadId > 0 ? threadId : null;
                });
                this.addLog('checkDraft:success', { hasDraft, threadId });
                return { code: 0, data: { hasDraft, threadId } };
            }
            this.addLog('checkDraft:api_failed', { ret });
            return ret;
        } catch (error) {
            this.addLog('checkDraft:error', { message: error?.message });
            return { code: -1, message: error.message || '查询失败' };
        }
    }

    /**
     * 恢复草稿：获取草稿详情并加载到表单
     * @param {number} threadId 草稿的 threadId
     * @returns {Promise<object>} 返回结果
     */
    @action.bound
    async restoreDraft(threadId) {
        this.addLog('restoreDraft:start', { threadId });
        try {
            if (!threadId) {
                const ret = { code: -1, message: '缺少草稿ID' };
                this.addLog('restoreDraft:no_id', ret);
                return ret;
            }

            // 获取草稿详情
            const detail = await this.fetchPostDetail(threadId);
            if (!detail) {
                const ret = { code: -1, message: '获取草稿失败' };
                this.addLog('restoreDraft:fetch_failed', ret);
                return ret;
            }

            // 加载到表单
            this.loadPostDetail(detail);
            
            // 标记为从草稿恢复
            runInAction(() => {
                this.draftData.isRestoreDraft = true;
                this.draftData.draftThreadId = threadId;
                this.postData.id = threadId;
                this.postData.isDraft = true; // 标记为草稿
            });

            this.addLog('restoreDraft:success', { threadId });
            return { code: 0, data: detail };
        } catch (error) {
            this.addLog('restoreDraft:error', { message: error?.message });
            return { code: -1, message: error.message || '恢复失败' };
        }
    }

    /**
     * 清除草稿标记
     */
    @action.bound
    clearDraft() {
        this.addLog('clearDraft');
        runInAction(() => {
            this.draftData.hasDraft = false;
            this.draftData.draftThreadId = null;
            this.draftData.isRestoreDraft = false;
        });
    }

    /**
     * 手动保存草稿
     * @param {File} coverFile 封面图文件对象（可选）
     * @param {File} videoFile 视频文件对象（可选）
     * @param {number} type 帖子类型，反馈帖传 2，普通贴传 0，默认 0
     * @returns {Promise<object>} 返回结果
     */
    @action.bound
    async saveDraftManually(coverFile = null, videoFile = null, type = 0) {
        this.addLog('saveDraftManually:start', { type });

        // 反馈帖强制校正类型，避免依赖 postData.type 时序
        if (type === 2 && this.postData.type !== 2) {
            runInAction(() => {
                this.postData.type = 2;
            });
        }

        // 检查是否有内容 - 需要检查文本或媒体内容
        const content = this.postData.content || '';
        if (!this.hasValidContent(content)) {
            const ret = { code: -1, message: '请输入内容' };
            this.addLog('saveDraftManually:no_content', ret);
            return ret;
        }

        this.setLoadingStatus('submit', true);

        try {
            // 上传附件
            const uploadResult = await this.uploadAllAttachments(coverFile, videoFile);
            if (!uploadResult.success) {
                this.addLog('saveDraftManually:attachments_failed', uploadResult);
                return uploadResult;
            }

            // 组合参数
            const params = this.getCreatePostParams();
            params.isDraft = true; // 标记为草稿保存

            // 根据是否有 threadId 决定调用哪个接口
            let ret;
            if (this.postData.id || this.draftData.draftThreadId) {
                // 修改草稿
                params.id = this.postData.id || this.draftData.draftThreadId;
                this.addLog('saveDraftManually:update', { id: params.id });
                ret = await updatePost(params);
            } else {
                // 创建新草稿
                this.addLog('saveDraftManually:create');
                ret = await createPost(params);
            }

            if (ret.code === 0 || ret.Code === 0) {
                const data = ret.Data || ret.data;
                // 更新 threadId
                if (data.id || data.threadId) {
                    runInAction(() => {
                        this.postData.id = data.id || data.threadId;
                        this.draftData.draftThreadId = data.id || data.threadId;
                        this.draftData.draftAutoSaveTime = new Date().toLocaleTimeString();
                        this.draftData.lastSaveType = 'manual'; // 标记为手动保存
                    });
                }
                this.addLog('saveDraftManually:success', { id: data.id || data.threadId });
            } else {
                this.addLog('saveDraftManually:api_failed', { ret });
            }
            return ret;
        } catch (error) {
            this.addLog('saveDraftManually:error', { message: error?.message });
            return { code: -1, message: error.message || '保存失败' };
        } finally {
            this.setLoadingStatus('submit', false);
            this.addLog('saveDraftManually:done');
        }
    }

    /**
     * 清空草稿（发布成功后调用）
     * @returns {Promise<object>} 返回结果
     */
    @action.bound
    async clearDraftAfterPublish() {
        this.addLog('clearDraftAfterPublish:start');
        try {
            const draftType = this.postData.type === 2 ? 2 : 0;
            const ret = await fetchDraft({ type: draftType, clear: 1 });
            if (ret.code === 0 || ret.Code === 0) {
                this.clearDraft();
                this.addLog('clearDraftAfterPublish:success');
            }
            return ret;
        } catch (error) {
            this.addLog('clearDraftAfterPublish:error', { message: error?.message });
            return { code: -1, message: error.message || '清除失败' };
        }
    }

    /**
     * 清空所有发帖数据，重置为初始状态
     * 用于页面离开或重新开始发帖时
     */
    @action
    clearPostData() {
        this.addLog('clearPostData:start', { 
            hasId: !!this.postData.id,
            hasContent: !!this.postData.content,
            hasTitle: !!this.postData.title 
        });

        // 重置发帖数据为初始状态
        this.postData = { ...initPostData };

        // 重置草稿相关状态
        this.draftData = {
            hasDraft: false,
            draftThreadId: null,
            isRestoreDraft: false,
            lastSaveType: 'manual',
        };

        // 清空话题缓存
        this.topicCache = {};

        // 重置加载状态
        this.loading = {
            submit: false,
            uploadCover: false,
        };

        this.addLog('clearPostData:complete', { success: true });
    }
}

export default SetPostAction;
